import { ActionIcon, Container, Flex, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconBoxMultiple, IconEdit, IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useAccount } from 'hooks/auth'
import { useDeleteShortage, useShortages } from 'hooks/shortage'
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  useMantineReactTable,
} from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useMemo, useState } from 'react'
import { Account } from 'types/auth'
import { Category } from 'types/products'
import { ShortageStatus, ShortageTableItem } from 'types/shortage'
import { categoriesForSelect, getCategoryName, getShortageStatusLabel, getUnitName } from 'utils'

type Props = {
  users?: Array<Account>
  group?: () => void
}

export default function ShortageTable({ users, group }: Props) {
  const deleteShortage = useDeleteShortage()

  function openUpdateShortageModal({ product, ...shortage }: ShortageTableItem) {
    modals.openContextModal({
      modal: 'updateShortage',
      centered: true,
      title: 'Edytuj brak produktu',
      innerProps: {
        shortage: {
          productId: product.id,
          productSymbol: product.symbol,
          ...shortage,
        },
      },
    })
  }

  function openDeleteShortageModal(shortage: ShortageTableItem) {
    modals.openConfirmModal({
      title: 'Usuń brak produktu',
      centered: true,
      children: (
        <Text size="sm">
          Czy na pewno chcesz usunąć brak produktu <b>{shortage.product.symbol}</b>? Nie będzie można cofnąć tej
          operacji.
        </Text>
      ),
      labels: { confirm: 'Usuń brak produktu', cancel: 'Nie chcę usuwać' },
      confirmProps: { color: 'red' },
      onConfirm: async () => await deleteShortage.mutateAsync(shortage.id),
    })
  }

  function openAddShortageModal() {
    modals.openContextModal({
      modal: 'addShortage',
      centered: true,
      title: 'Dodaj brak produktu',
      innerProps: {},
    })
  }

  const columns = useMemo<Array<MRT_ColumnDef<ShortageTableItem>>>(
    () => [
      {
        accessorKey: 'product.symbol',
        header: 'Symbol produktu',
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po symbolu',
        },
      },
      {
        accessorKey: 'size',
        header: 'Wymiar',
        size: 160,
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po wymiarze',
        },
        Cell: ({ cell }) => {
          const size = cell.getValue<string>()
          return `${size} cm`
        },
      },
      {
        accessorKey: 'category',
        header: 'Kategoria',
        filterVariant: 'select',
        mantineFilterSelectProps: {
          placeholder: 'Filtruj po kategorii',
          data: categoriesForSelect,
        },
        Cell: ({ cell }) => {
          const category = cell.getValue<Category>()
          const categoryName = getCategoryName(category, { capitalize: true }).mianownik
          return categoryName
        },
      },
      {
        accessorKey: 'feature',
        header: 'Cecha',
        size: 150,
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po cesze',
        },
      },
      {
        accessorKey: 'amount',
        header: 'Ilość',
        size: 120,
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po ilości',
        },
        Cell: ({ cell }) => {
          const amount = cell.getValue<string>()
          const unit = cell.row.original.unit
          if (!amount) return ''
          return `${amount} ${getUnitName(unit).short}`
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Data dodania',
        filterVariant: 'date',
        Cell: ({ cell }) => {
          const createdAt = cell.getValue<Date>()
          if (!createdAt) return ''
          return dayjs(createdAt).format('D MMMM HH:mm')
        },
        mantineFilterDateInputProps: {
          placeholder: 'Filtruj po dacie dodania',
          locale: 'pl',
          valueFormat: 'D MMMM',
          preserveTime: false,
        },
        size: 200,
      },
      {
        accessorKey: 'orderNumber',
        header: 'Numer zamówienia',
        size: 230,
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po numerze zamówienia',
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        filterVariant: 'select',
        mantineFilterSelectProps: {
          placeholder: 'Filtruj po statusie',
          data: Object.values(ShortageStatus).map((status) => ({
            value: status,
            label: getShortageStatusLabel(status),
          })),
        },
        Cell: ({ cell }) => {
          const status = cell.getValue<ShortageStatus>()
          if (!status) return ''
          return getShortageStatusLabel(status)
        },
        size: 175,
      },
      ...(users
        ? [
            {
              accessorKey: 'createdById',
              header: 'Dodane przez',
              filterVariant: 'select',
              mantineFilterSelectProps: {
                placeholder: 'Filtruj po użytkowniku',
                data: users.map((user) => ({
                  value: user.id,
                  label: `${user.firstName} ${user.lastName}`,
                })),
              },
              Cell: ({ cell }) => {
                const createdById = cell.getValue<string>()
                if (!createdById) return ''
                const user = users.find((user) => user.id === createdById)
                return `${user?.firstName} ${user?.lastName}`
              },
            } as MRT_ColumnDef<ShortageTableItem>,
          ]
        : []),
      {
        accessorKey: 'comments',
        header: 'Uwagi',
        mantineFilterTextInputProps: {
          placeholder: 'Filtruj po uwagach',
        },
      },
      {
        accessorKey: 'orderedAt',
        header: 'Data zamówienia',
        filterVariant: 'date',
        Cell: ({ cell }) => {
          const orderedAt = cell.getValue<Date>()
          return orderedAt ? dayjs(orderedAt).format('D MMMM HH:mm') : ''
        },
        mantineFilterDateInputProps: {
          placeholder: 'Filtruj po dacie zamówienia',
          locale: 'pl',
        },
        size: 200,
      },
      {
        accessorKey: 'fulfilledAt',
        header: 'Data realizacji',
        filterVariant: 'date',
        Cell: ({ cell }) => {
          const fulfilledAt = cell.getValue<Date>()
          return fulfilledAt ? dayjs(fulfilledAt).format('D MMMM HH:mm') : ''
        },
        mantineFilterDateInputProps: {
          placeholder: 'Filtruj po dacie realizacji',
          locale: 'pl',
        },
        size: 200,
      },
    ],
    []
  )

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const { data: account } = useAccount()

  const { data, isLoading, isError, error, isFetching, refetch } = useShortages(
    users === undefined ? account.id : undefined,
    { columnFilters, pagination }
  )

  const memoizedData = useMemo(() => data ?? { data: [], total: 0 }, [JSON.stringify(data)])
  const table = useMantineReactTable({
    columns,
    data: memoizedData.data,
    layoutMode: 'grid',
    rowCount: memoizedData.total,
    state: {
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      showColumnFilters: true,
      pagination,
      columnFilters,
    },
    initialState: {
      showGlobalFilter: false,
      showColumnFilters: true,
      columnPinning: { right: ['mrt-row-actions'] },
    },
    enableExpanding: false,
    enableGlobalFilterModes: false,
    enableSorting: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableGlobalFilter: false,
    enableHiding: false,
    enableColumnActions: false,
    enableColumnFilters: true,
    enableBottomToolbar: true,
    enableColumnPinning: true,
    manualFiltering: true,
    manualPagination: true,
    paginateExpandedRows: false,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    renderTopToolbarCustomActions: () => (
      <Container mr={0} pr={0}>
        {!!users && group && (
          <Tooltip label="Grupuj braki według produktu">
            <ActionIcon size="lg" variant="subtle" onClick={group} ml="auto" mr={4}>
              <IconBoxMultiple />
            </ActionIcon>
          </Tooltip>
        )}
        <Tooltip label="Dodaj brak produktu">
          <ActionIcon size="lg" variant="subtle" onClick={() => openAddShortageModal()} ml="auto" mr={4}>
            <IconPlus />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Odśwież dane">
          <ActionIcon
            size="lg"
            variant="subtle"
            color="gray"
            onClick={() => {
              refetch()
            }}
          >
            <IconRefresh />
          </ActionIcon>
        </Tooltip>
      </Container>
    ),
    mantineToolbarAlertBannerProps: isError
      ? {
          color: 'red',
          children: `Wystąpił błąd podczas pobierania danych. (${error})`,
        }
      : undefined,
    localization: MRT_Localization_PL,
    enableRowActions: true,
    positionActionsColumn: 'last',
    mantinePaginationProps: {
      rowsPerPageOptions: ['5', '10', '15'],
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 100,
        enablePinning: true,
      },
    },
    renderRowActions: ({ row }) => {
      return (
        <Flex gap="sm" justify="end">
          <Tooltip
            label={
              row.original.status === ShortageStatus.NEW || row.original.status === ShortageStatus.UNFULFILLED
                ? 'Edytuj'
                : `Nie można edytować ${getStatusInDopelniacz(row.original.status)} braków`
            }
          >
            <ActionIcon
              variant="light"
              color="blue"
              radius="xl"
              onClick={() => openUpdateShortageModal(row.original)}
              disabled={
                row.original.status !== ShortageStatus.NEW && row.original.status !== ShortageStatus.UNFULFILLED
              }
            >
              <IconEdit size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip
            label={
              row.original.status === ShortageStatus.NEW || row.original.status === ShortageStatus.UNFULFILLED
                ? 'Usuń'
                : `Nie można usunąć ${getStatusInDopelniacz(row.original.status)} braków`
            }
          >
            <ActionIcon
              radius="xl"
              variant="light"
              color="red"
              onClick={() => openDeleteShortageModal(row.original)}
              disabled={
                row.original.status !== ShortageStatus.NEW && row.original.status !== ShortageStatus.UNFULFILLED
              }
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        </Flex>
      )
    },
  })

  return <MantineReactTable table={table} />
}

function getStatusInDopelniacz(status: ShortageStatus) {
  switch (status) {
    case ShortageStatus.NEW:
      return 'nowych'
    case ShortageStatus.ORDERED:
      return 'zamówionych'
    case ShortageStatus.FULFILLED:
      return 'zrealizowanych'
    case ShortageStatus.UNFULFILLED:
      return 'niezrealizowanych'
  }
}
